import useReveal from '../hooks/useReveal';

/**
 * Finale — the primary conversion. Two equal-weight CTAs: email (free
 * written critique) and phone/text. Small-business owners disproportionately
 * prefer a call to a form, so the tel: action is a first-class button.
 *
 * Above the CTAs sits an offer anchor — a small framed line that tells
 * the visitor what to expect (free critique) and what's deliberately NOT
 * on the page (a price). Premium service positioning: pricing happens on
 * the call, not in a list. The line replaces an earlier explicit price
 * range — that range was useful for self-qualifying leads but ultimately
 * read as discount-store inventory rather than a custom-scoped studio.
 */
export default function Finale() {
  const r1 = useReveal<HTMLParagraphElement>();
  const r2 = useReveal<HTMLHeadingElement>();
  const r3 = useReveal<HTMLParagraphElement>();
  const r4 = useReveal<HTMLParagraphElement>();
  const r5 = useReveal<HTMLDivElement>();
  const r6 = useReveal<HTMLParagraphElement>();

  return (
    <section id="contact" className="section finale">
      <div className="wrap">
        <p ref={r1} className="eyebrow reveal">
          <span className="dot" aria-hidden="true" />
          Let’s talk
        </p>
        <h2 ref={r2} className="reveal d1">
          Let’s rebuild your website
          <br />
          <em className="serif">properly.</em>
        </h2>
        <p ref={r3} className="reveal d2">
          Tell me what’s bothering you about your current site. I’ll send back a written critique
          within 48 hours — short, specific, yours to keep whether we work together or not.
        </p>

        {/* Offer anchor — replaces the older price range. Sets expectations
            without quoting numbers: every project gets scoped on the call,
            with a free written critique as the entry hook. */}
        <p ref={r4} className="offer-anchor reveal d2">
          <span className="offer-headline">Custom-scoped projects</span>
          <span className="offer-note">Free critique to start · Pricing discussed on the call</span>
        </p>

        <div ref={r5} className="actions reveal d3">
          <a
            className="btn btn-primary"
            href="mailto:hello@webnovaco.com?subject=WebNova%20%E2%80%94%20Free%20site%20critique"
          >
            Request a free critique
            <ArrowRight />
          </a>
          <a className="btn btn-call" href="tel:+17038887050" aria-label="Call or text 703-888-7050">
            <PhoneIcon />
            Call or text (703)&nbsp;888-7050
          </a>
        </div>
        <p ref={r6} className="small reveal d4">
          Replies within 24 hours · hello@webnovaco.com · (703) 888-7050
        </p>
      </div>
    </section>
  );
}

function ArrowRight() {
  return (
    <span className="arrow" aria-hidden="true">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 12h14M13 5l7 7-7 7" />
      </svg>
    </span>
  );
}

function PhoneIcon() {
  return (
    <span className="phone-ico" aria-hidden="true">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
      </svg>
    </span>
  );
}
